'use client';

import React from 'react';
import { Label } from '@/components/ui/label';

type LayoutType =
  | 'speaker'
  | 'grid'
  | 'interview'
  | 'podcast'
  | 'webinar'
  | 'panel'
  | 'loom'
  | 'captioned'
  | 'audio-visual';

type BubbleSize = 'small' | 'medium' | 'large' | 'xl';
type BubbleShape = 'circle' | 'rounded-rect';
type LoomBubblePosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'right-panel' | 'left-panel';

interface RecordingPreferences {
  logoPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  logoSize?: 'small' | 'medium' | 'large';
  logoOpacity?: number;
  bgGradientStart?: string | null;
  bgGradientEnd?: string | null;
  transitionStyle?: 'smooth' | 'professional' | 'dynamic' | 'instant';
}

type BackgroundPattern = 'none' | 'grid' | 'dots' | 'crosshatch';
type SeparatorStyle = 'line' | 'thick' | 'glow' | 'none';
type TrackPadding = 'none' | 'small' | 'medium' | 'large';

interface RecordingPreviewProps {
  preferences: RecordingPreferences;
  logoUrl?: string | null;
  themeColor?: string | null;
  layout?: LayoutType;
  bubbleSize?: BubbleSize;
  bubbleShape?: BubbleShape;
  bubblePosition?: LoomBubblePosition;
  backgroundPattern?: BackgroundPattern;
  separatorStyle?: SeparatorStyle;
  trackPadding?: TrackPadding;
  compact?: boolean;
}

const BUBBLE_DIAMETER: Record<BubbleSize, number> = {
  small: 32,
  medium: 44,
  large: 56,
  xl: 68,
};

function MockSpeaker({ letter, color, name }: { letter: string; color: string; name: string }) {
  return (
    <div className="flex-1 h-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center relative overflow-hidden shadow-xl border-3 border-white/20">
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      <div className="relative z-10 text-center">
        <div className={`w-16 h-16 mx-auto mb-2 ${color} rounded-full flex items-center justify-center`}>
          <span className="text-2xl font-bold text-white">{letter}</span>
        </div>
        <span className="text-white text-sm font-medium">{name}</span>
      </div>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-black/85 backdrop-blur-sm rounded-lg">
        <span className="text-white text-xs font-semibold">{name}</span>
      </div>
    </div>
  );
}

function MockSpeakerCompact({ letter, color }: { letter: string; color: string }) {
  return (
    <div className="flex-1 h-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center relative overflow-hidden border border-white/10">
      <div className={`w-8 h-8 ${color} rounded-full flex items-center justify-center`}>
        <span className="text-sm font-bold text-white">{letter}</span>
      </div>
    </div>
  );
}

const PADDING_CLASS: Record<TrackPadding, string> = {
  none: 'p-0',
  small: 'p-1.5',
  medium: 'p-3',
  large: 'p-5',
};

const LOOM_PADDING_CLASS: Record<TrackPadding, string> = {
  none: 'p-0',
  small: 'p-2',
  medium: 'p-4',
  large: 'p-6',
};

function LayoutMockup({ layout, bubbleSize = 'medium', bubbleShape = 'circle', bubblePosition = 'bottom-left', separatorStyle = 'line', trackPadding = 'medium' }: { layout?: LayoutType; bubbleSize?: BubbleSize; bubbleShape?: BubbleShape; bubblePosition?: LoomBubblePosition; separatorStyle?: SeparatorStyle; trackPadding?: TrackPadding }) {
  const bubblePx = BUBBLE_DIAMETER[bubbleSize];
  const isPanelMode = bubblePosition === 'right-panel' || bubblePosition === 'left-panel';
  const pad = PADDING_CLASS[trackPadding];
  const loomPad = LOOM_PADDING_CLASS[trackPadding];

  switch (layout) {
    case 'loom':
      if (isPanelMode) {
        const isLeft = bubblePosition === 'left-panel';
        return (
          <div className={`absolute inset-0 ${loomPad} flex gap-2`}>
            {isLeft && (
              <div className="w-[30%] h-full bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center border border-white/10">
                <span className="text-white text-xs font-bold">A</span>
              </div>
            )}
            <div className="flex-1 h-full bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center border border-white/10">
              <span className="text-white/40 text-xs">Screen Share</span>
            </div>
            {!isLeft && (
              <div className="w-[30%] h-full bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center border border-white/10">
                <span className="text-white text-xs font-bold">A</span>
              </div>
            )}
          </div>
        );
      }
      const cornerClasses: Record<string, string> = {
        'top-left': 'top-5 left-5',
        'top-right': 'top-5 right-5',
        'bottom-left': 'bottom-5 left-5',
        'bottom-right': 'bottom-5 right-5',
      };
      const posClass = cornerClasses[bubblePosition] || 'bottom-5 left-5';

      return (
        <div className={`absolute inset-0 ${loomPad}`}>
          <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center border border-white/10">
            <span className="text-white/40 text-xs">Screen Share</span>
          </div>
          <div
            className={`absolute ${posClass} ${bubbleShape === 'rounded-rect' ? 'rounded-xl' : 'rounded-full'} bg-gradient-to-br from-gray-600 to-gray-700 border-2 border-white/30 flex items-center justify-center shadow-lg`}
            style={{ width: bubblePx, height: bubblePx }}
          >
            <span className="text-white text-xs font-bold">A</span>
          </div>
        </div>
      );

    case 'grid':
      return (
        <div className={`absolute inset-0 ${pad} grid grid-cols-2 grid-rows-2 gap-2`}>
          <MockSpeakerCompact letter="A" color="bg-red-500" />
          <MockSpeakerCompact letter="B" color="bg-pink-500" />
          <MockSpeakerCompact letter="C" color="bg-blue-500" />
          <MockSpeakerCompact letter="D" color="bg-green-500" />
        </div>
      );

    case 'interview': {
      const dividerStyles: Record<string, string> = {
        none: 'w-0',
        line: 'w-[2px] bg-white/15',
        thick: 'w-[4px] bg-white/12 rounded-sm',
        glow: 'w-[3px] bg-red-500/50 shadow-[0_0_8px_2px_rgba(239,57,55,0.3)]',
      };
      const dividerClass = dividerStyles[separatorStyle] || dividerStyles.line;
      return (
        <div className={`absolute inset-0 ${pad} flex gap-1 items-center`}>
          <MockSpeakerCompact letter="A" color="bg-red-500" />
          {separatorStyle !== 'none' && (
            <div className={`${dividerClass} h-3/4 rounded-full`} />
          )}
          <MockSpeakerCompact letter="B" color="bg-pink-500" />
        </div>
      );
    }

    case 'speaker':
      return (
        <div className={`absolute inset-0 ${pad} flex gap-2`}>
          <div className="flex-[4] h-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center border border-white/10">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-lg font-bold text-white">A</span>
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <MockSpeakerCompact letter="B" color="bg-pink-500" />
            <MockSpeakerCompact letter="C" color="bg-blue-500" />
          </div>
        </div>
      );

    case 'podcast':
    case 'audio-visual':
      return (
        <div className={`absolute inset-0 ${pad} flex gap-6 items-center justify-center`}>
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 bg-red-500 rounded-full flex items-center justify-center border-2 border-white/20">
              <span className="text-lg font-bold text-white">A</span>
            </div>
            <div className="w-12 h-3 bg-white/10 rounded" />
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 bg-pink-500 rounded-full flex items-center justify-center border-2 border-white/20">
              <span className="text-lg font-bold text-white">B</span>
            </div>
            <div className="w-12 h-3 bg-white/10 rounded" />
          </div>
        </div>
      );

    case 'webinar':
      return (
        <div className={`absolute inset-0 ${pad}`}>
          <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center border border-white/10">
            <span className="text-white/40 text-xs">Screen Share</span>
          </div>
          <div className="absolute bottom-5 right-5 w-16 h-10 bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg border border-white/20 flex items-center justify-center shadow-lg">
            <span className="text-white text-xs font-bold">A</span>
          </div>
        </div>
      );

    case 'panel':
      return (
        <div className={`absolute inset-0 ${pad} flex flex-col gap-2`}>
          <div className="flex-1 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center border border-white/10">
            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-white">A</span>
            </div>
          </div>
          <div className="h-10 flex gap-2">
            <MockSpeakerCompact letter="B" color="bg-pink-500" />
            <MockSpeakerCompact letter="C" color="bg-blue-500" />
            <MockSpeakerCompact letter="D" color="bg-green-500" />
          </div>
        </div>
      );

    default:
      return (
        <div className={`absolute inset-0 ${pad} flex gap-3 items-center`}>
          <MockSpeakerCompact letter="A" color="bg-red-500" />
          <MockSpeakerCompact letter="B" color="bg-pink-500" />
        </div>
      );
  }
}

export function RecordingPreview({
  preferences,
  logoUrl,
  themeColor,
  layout,
  bubbleSize,
  bubbleShape,
  bubblePosition,
  backgroundPattern,
  separatorStyle,
  trackPadding,
  compact = false,
}: RecordingPreviewProps) {
  const logoSizes = {
    small: '60px',
    medium: '90px',
    large: '120px',
  };

  const logoSizeVal = compact ? '40px' : logoSizes[preferences.logoSize || 'medium'];

  const bgStart = preferences.bgGradientStart || themeColor || '#FF8C42';
  const bgEnd = preferences.bgGradientEnd || themeColor || '#FFB84D';
  const bgGradient = `linear-gradient(135deg, ${bgStart} 0%, ${bgEnd} 100%)`;

  const logoPositionClasses = {
    'top-right': 'top-3 right-3',
    'top-left': 'top-3 left-3',
    'bottom-right': 'bottom-3 right-3',
    'bottom-left': 'bottom-3 left-3',
  };

  const positionClass = logoPositionClasses[preferences.logoPosition || 'top-right'];
  const logoOpacity = preferences.logoOpacity ?? 0.8;

  const useLayoutMockup = !!layout;

  return (
    <div className={compact ? '' : 'space-y-3'}>
      {!compact && <Label className="text-sm font-medium">Preview</Label>}

      <div
        className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-700 shadow-lg"
        style={{ background: bgGradient }}
      >
        {backgroundPattern && backgroundPattern !== 'none' && (
          <div className="absolute inset-0 z-[1] pointer-events-none opacity-[0.08]" style={
            backgroundPattern === 'grid' ? {
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            } : backgroundPattern === 'dots' ? {
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)',
              backgroundSize: '12px 12px',
            } : backgroundPattern === 'crosshatch' ? {
              backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(-45deg, rgba(255,255,255,0.2) 1px, transparent 1px)',
              backgroundSize: '10px 10px',
            } : {}
          } />
        )}

        {useLayoutMockup ? (
          <LayoutMockup layout={layout} bubbleSize={bubbleSize} bubbleShape={bubbleShape} bubblePosition={bubblePosition} separatorStyle={separatorStyle} trackPadding={trackPadding} />
        ) : (
          <div className="absolute inset-0 p-6 flex gap-4 items-center">
            <MockSpeaker letter="A" color="bg-red-500" name="Speaker 1" />
            <MockSpeaker letter="B" color="bg-pink-500" name="Speaker 2" />
          </div>
        )}

        {logoUrl && (
          <div className={`absolute ${positionClass} z-50`}>
            <img
              src={logoUrl}
              alt="Domain Logo"
              className="object-contain drop-shadow-lg"
              style={{
                width: logoSizeVal,
                height: 'auto',
                maxHeight: logoSizeVal,
                opacity: logoOpacity
              }}
            />
          </div>
        )}

        <div className={`absolute ${compact ? 'top-2 left-2 px-2 py-1' : 'top-3 left-3 px-3 py-1.5'} flex items-center gap-2 bg-red-600 rounded-full`}>
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          <span className="text-white text-xs font-semibold">REC</span>
        </div>
      </div>

      {!compact && (
        <>
          <p className="text-xs text-muted-foreground text-center">
            This is how your recorded conversations will appear
          </p>

          {preferences.transitionStyle && preferences.transitionStyle !== 'smooth' && (
            <p className="text-xs text-red-600 dark:text-red-400 text-center">
              Using <span className="font-semibold">{preferences.transitionStyle}</span> speaker transitions
            </p>
          )}
        </>
      )}
    </div>
  );
}
